import { useNode } from "@craftjs/core";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { CardBlockSettings } from "@/components/craft/settings/CardBlockSettings";

type CardBlockProps = {
  title: string;
  text: string;
  imageSrc?: string;
  imageMediaId?: string | null;
  buttonLabel?: string;
  buttonHref?: string;
  style?: ResponsiveStyleProps;
};

export function CardBlock({
  title = "Card title",
  text = "Card description text.",
  imageSrc = "",
  buttonLabel = "",
  buttonHref = "",
  style = defaultStyleProps(),
}: CardBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));
  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  return (
    <article
      ref={(ref) => { if (ref) connect(drag(ref)); }}
      className={cn(
        "my-6 overflow-hidden rounded-xl border bg-card shadow-sm",
        `craft-node-${id}`,
        styleClass,
        selected && "outline outline-2 outline-dashed outline-primary/30",
      )}
      style={inlineStyle}
    >
      {imageSrc && (
        <img src={imageSrc} alt="" className="aspect-[16/10] w-full object-cover" />
      )}
      <div className="p-6">
        <h3 className="text-xl font-bold text-primary">{title}</h3>
        <p className="mt-2 text-foreground/80">{text}</p>
        {buttonLabel && buttonHref && (
          <Link to={buttonHref as never} className="mt-4 inline-block text-sm font-semibold text-gold hover:underline">
            {buttonLabel}
          </Link>
        )}
      </div>
    </article>
  );
}

CardBlock.craft = {
  displayName: "Card",
  props: {
    title: "Card title",
    text: "Card description text.",
    imageSrc: "",
    imageMediaId: null,
    buttonLabel: "",
    buttonHref: "",
    style: defaultStyleProps(),
  },
  rules: { canDrag: () => true },
  related: { settings: CardBlockSettings },
};
